import {useRef} from 'react';
import {Close, Content, Overlay, Portal, Title} from '@radix-ui/react-dialog';
import {RecipeVariants} from '@vanilla-extract/recipes';
import cx from 'classnames';

import {Button} from '#component/base/Button';
import {Text} from '#component/base/Text';
import {sprinkles} from '#style/sprinkles';
import {ExtractRecipeVariants} from '#util/vanilla-extract';

import {ModalProps} from './Modal.types';

import * as css from './Modal.css';

const getHeaderColor = (variant: ModalProps['variant']) =>
  variant === 'dark' ? 'white' : 'burgundy';

const getButtonVariant = (variant: ModalProps['variant']) =>
  variant === 'dark' ? 'secondary-dark' : 'secondary';

type BackdropVariant = keyof ExtractRecipeVariants<RecipeVariants<typeof css.backdrop>>['variant'];

export const Modal = ({
  children,
  className,
  contentClassName,
  bodyClassName,
  footer,
  header,
  onOpenAutoFocus,
  hideCloseButton = false,
  layout = 'primary',
  variant: variantPropValue,
  ...otherProps
}: ModalProps) => {
  let backdropVariant: BackdropVariant = 'semiTransparent';
  if (layout === 'fullScreen') {
    backdropVariant = 'none';
  }

  let variant = variantPropValue;
  if (!variant) {
    if (layout === 'fullScreen') {
      variant = 'dark';
    } else {
      variant = 'light';
    }
  }

  const footerRef = useRef<HTMLElement>(null);

  const handleOnOpenAutoFocus = (e: Event) => {
    const focusableFooterEl = footerRef.current?.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    if (!!focusableFooterEl) {
      e.preventDefault();
      (focusableFooterEl as HTMLElement).focus();
    } else {
      onOpenAutoFocus?.(e);
    }
  };

  return (
    <Portal>
      <Overlay className={css.backdrop({variant: backdropVariant})} />
      <Content className={css.dialogRoot({layout})} onOpenAutoFocus={handleOnOpenAutoFocus}>
        <div className={cx(css.dialogContent({layout, variant}), className)} {...otherProps}>
          {!header ? null : (
            <header className={css.header}>
              <Text as={Title} className={css.headerContent} color={getHeaderColor(variant)}>
                {header}
              </Text>
              {hideCloseButton ? null : (
                <Button
                  aria-label="Close dialog"
                  className={sprinkles({pos: 'relative'})}
                  as={Close}
                  icon="X"
                  variant={getButtonVariant(variant)}
                />
              )}
            </header>
          )}
          <div className={cx(css.body({layout}), bodyClassName)}>
            <div className={cx(css.content, contentClassName)}>{children}</div>
            {!!footer && (
              <footer className={css.footer} ref={footerRef}>
                {footer}
              </footer>
            )}
          </div>
        </div>
      </Content>
    </Portal>
  );
};
